import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, Users, TrendingUp, Mail, Phone, Building2, Linkedin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    serviceInterest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success('Thank you! Your message has been sent successfully.');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          serviceInterest: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again or contact us directly at info@hawk-consult.be');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, serviceInterest: value }));
  };

  return (
    <div className="hawk-site">
      {/* Header */}
      <header className="dark-header">
        <div className="header-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_242b8e63-9171-43b5-a223-e3eda848d07a/artifacts/pm73ws30_Color%20logo%20with%20background.png" 
            alt="Hawk Consult" 
            className="dark-logo"
          />
          <nav className="dark-nav">
            <a href="#home" className="dark-nav-link">Home</a>
            <a href="#about" className="dark-nav-link">About</a>
            <a href="#services" className="dark-nav-link">Services</a>
            <a href="#team" className="dark-nav-link">Team</a>
            <a href="#contact" className="dark-nav-link">Contact</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_242b8e63-9171-43b5-a223-e3eda848d07a/artifacts/pm73ws30_Color%20logo%20with%20background.png" 
            alt="Hawk Consult" 
            className="hero-logo"
          />
          <h1 className="hero-title">We Lift Your Business</h1>
          <p className="hero-subtitle">
            Strategic management consultancy for business development and HR excellence.
            <br />Elevating high-tech companies through expert guidance and proven methodologies.
          </p>
          <div className="hero-cta">
            <Button className="btn-primary" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
              Get Started <ArrowRight size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="section-container">
          <h2 className="section-title">About Hawk Consult</h2>
          <div className="about-content">
            <p className="about-text">
              Hawk Consult BV is a premier management consultancy firm specializing in business development 
              for high-tech products and HR integration strategies for corporate acquisitions. Founded by 
              two seasoned professionals with decades of experience in technology and human resources, 
              we bring unparalleled expertise to organizations seeking transformational growth.
            </p>
            <p className="about-text">
              Our approach combines deep technical knowledge with strategic business acumen, ensuring 
              that every engagement delivers measurable results. Whether you're launching innovative 
              products or integrating acquired companies, Hawk Consult provides the guidance you need 
              to soar above the competition.
            </p>
          </div>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">40+</div>
              <div className="stat-label">Years Combined Experience</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">2</div>
              <div className="stat-label">Expert Partners</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">100%</div>
              <div className="stat-label">Client Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="section-container">
          <h2 className="section-title">Our Services</h2>
          <div className="services-grid">
            <Card className="service-card">
              <div className="service-icon">
                <TrendingUp size={48} />
              </div>
              <h3 className="service-title">Business Development</h3>
              <p className="service-description">
                Strategic business development for high-tech products requiring strong technical foundations. 
                We specialize in IoT, RFID technologies, smart sensors, and energy solutions.
              </p>
              <ul className="service-list">
                <li>Product Management</li>
                <li>Sales Management</li>
                <li>Partner & Customer Acquisition</li>
                <li>Market Entry Strategies</li>
                <li>Technical Sales Support</li>
              </ul>
            </Card>

            <Card className="service-card">
              <div className="service-icon">
                <Users size={48} />
              </div>
              <h3 className="service-title">HR Consultancy</h3>
              <p className="service-description">
                Expert guidance for HR integration during corporate acquisitions and organizational transformations. 
                Leveraging global HR leadership experience in the semiconductor industry.
              </p>
              <ul className="service-list">
                <li>Acquisition HR Integration</li>
                <li>Organizational Alignment</li>
                <li>HR Strategy Development</li>
                <li>Change Management</li>
                <li>Leadership Development</li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="team-section">
        <div className="section-container">
          <h2 className="section-title">Our Partners</h2>
          <div className="team-grid">
            <Card className="team-card">
              <div className="team-photo">
                <img 
                  src="https://customer-assets.emergentagent.com/job_bizdev-hr-consult/artifacts/9sgd9ayo_Patrick_200x200.png" 
                  alt="Patrick Willekens"
                  className="team-photo-img"
                />
              </div>
              <h3 className="team-name">Patrick Willekens</h3>
              <div className="team-role">Business Development Consultant</div>
              <p className="team-bio">
                With 20 years in product management for technology products and 15 years in sales management 
                and business development, Patrick brings deep expertise in products requiring strong technical 
                foundations. His electronic engineering degree enables him to bridge technology and business seamlessly.
              </p>
              <div className="team-specialties">
                <strong>Specialties:</strong> Product Management, Sales Management, Program Management for IoT and RFID technologies
              </div>
              <div className="team-current">
                <strong>Current Engagements:</strong>
                <ul>
                  <li><strong>Option (since Nov 2024):</strong> Business development for DIN-rail metering gateways, smart parking sensors, and engineering services</li>
                  <li><strong>BeLoaded (since May 2025):</strong> Finding partners and customers for large battery installations for load shifting and peak shaving</li>
                </ul>
              </div>
              <a 
                href="https://www.linkedin.com/in/patrick-willekens" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="linkedin-link"
              >
                <Linkedin size={20} /> View LinkedIn Profile
              </a>
            </Card>

            <Card className="team-card">
              <div className="team-photo">
                <img 
                  src="https://customer-assets.emergentagent.com/job_bizdev-hr-consult/artifacts/i08hr1ja_EstherHouben.jpg" 
                  alt="HR Partner"
                  className="team-photo-img"
                />
              </div>
              <h3 className="team-name">HR Partner</h3>
              <div className="team-role">HR Integration Consultant</div>
              <p className="team-bio">
                With 20 years at a large semiconductor company, she is part of global HR leadership as Senior Director 
                for Automotive Embedded Systems, overseeing multiple businesses including Automotive Processors, 
                Microcontrollers, Radar, Audio Systems, and Automotive Systems.
              </p>
              <div className="team-specialties">
                <strong>Expertise:</strong> HR strategy alignment with organizational goals, specializing in HR integrations 
                of acquired companies such as the recent acquisition of a large automotive software player
              </div>
              <div className="team-current">
                <strong>Key Achievements:</strong>
                <ul>
                  <li>Global HR leadership across multiple automotive technology businesses</li>
                  <li>Successfully integrated acquired companies including a large automotive software player</li>
                  <li>Strategic HR alignment for complex organizational structures</li>
                </ul>
              </div>
              <a 
                href="https://www.linkedin.com/in/esther-houben-3a97a9" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="linkedin-link"
              >
                <Linkedin size={20} /> View LinkedIn Profile
              </a>
            </Card>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="case-studies" className="case-studies-section">
        <div className="section-container">
          <h2 className="section-title">Case Studies</h2>
          <div className="case-studies-grid">
            <Card className="case-study-card">
              <h3 className="case-study-title">Option: Smart IoT Solutions</h3>
              <div className="case-study-meta">Business Development | Since November 2024</div>
              <p className="case-study-description">
                Patrick is driving business development for Option's innovative product portfolio, including 
                DIN-rail metering gateways and smart parking sensors. Additionally, he's expanding the reach 
                of Option's engineering services, which encompass sensor design, gateway design, and antenna 
                design and tuning.
              </p>
              <div className="case-study-impact">
                <strong>Impact Areas:</strong>
                <ul>
                  <li>Market expansion for smart metering solutions</li>
                  <li>Partnership development for IoT technologies</li>
                  <li>Technical sales support for engineering services</li>
                </ul>
              </div>
            </Card>

            <Card className="case-study-card">
              <h3 className="case-study-title">BeLoaded: Energy Storage Solutions</h3>
              <div className="case-study-meta">Business Development | Since May 2025</div>
              <p className="case-study-description">
                Patrick is spearheading the identification of strategic partners and customers for large battery 
                installation projects. These installations serve critical functions including load shifting, 
                peak shaving, and revenue generation for energy management applications.
              </p>
              <div className="case-study-impact">
                <strong>Impact Areas:</strong>
                <ul>
                  <li>Strategic partnership development in energy sector</li>
                  <li>Customer acquisition for battery storage solutions</li>
                  <li>Revenue optimization through energy management</li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <h2 className="section-title">Get In Touch</h2>
          <p className="contact-intro">
            Ready to elevate your business? Contact us to discuss how Hawk Consult can help you achieve your goals.
          </p>
          <div className="contact-wrapper">
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Name *</label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="Your name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email *</label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="your.email@company.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company" className="form-label">Company *</label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="Your company name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone</label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="+32 XXX XX XX XX"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="serviceInterest" className="form-label">Service Interest *</label>
                <Select onValueChange={handleSelectChange} value={formData.serviceInterest} required>
                  <SelectTrigger className="form-select">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="business-development">Business Development</SelectItem>
                    <SelectItem value="hr-integration">HR Integration Consultancy</SelectItem>
                    <SelectItem value="both">Both Services</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">Message *</label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Tell us about your needs..."
                  rows={6}
                />
              </div>

              <Button type="submit" className="btn-primary submit-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'} <ArrowRight size={20} />
              </Button>
            </form>

            <div className="contact-info">
              <div className="contact-info-item">
                <Mail size={24} color="#D4AF37" />
                <div>
                  <div className="contact-info-label">Email</div>
                  <a href="mailto:info@hawk-consult.be" className="contact-info-value">
                    info@hawk-consult.be
                  </a>
                </div>
              </div>
              <div className="contact-info-item">
                <Building2 size={24} color="#D4AF37" />
                <div>
                  <div className="contact-info-label">Address</div>
                  <div className="contact-info-value">
                    Mastellestraat 15<br />
                    3020 Veltem-Beisem<br />
                    Belgium
                  </div>
                </div>
              </div>
              <div className="contact-info-item">
                <Building2 size={24} color="#D4AF37" />
                <div>
                  <div className="contact-info-label">Company Number</div>
                  <div className="contact-info-value">
                    BE1017.690.742
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_242b8e63-9171-43b5-a223-e3eda848d07a/artifacts/pm73ws30_Color%20logo%20with%20background.png" 
              alt="Hawk Consult" 
              className="footer-logo-img"
            />
            <p className="footer-tagline">We Lift Your Business</p>
          </div>
          <div className="footer-links">
            <a href="#home" className="footer-link">Home</a>
            <a href="#about" className="footer-link">About</a>
            <a href="#services" className="footer-link">Services</a>
            <a href="#team" className="footer-link">Team</a>
            <a href="#contact" className="footer-link">Contact</a>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 Hawk Consult BV. All rights reserved.</p>
            <p className="footer-domain">www.hawk-consult.be</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;